import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Button, FormContainer, RHFTextField } from '@openx/components/core';
import qs from 'query-string';
import { type FormEventHandler, type MouseEventHandler, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { object, string } from 'yup';

export const EMAIL_FIELD = 'login';
export const PASSWORD_FIELD = 'password';

export interface LoginFormProps {
  onSubmitHandler: FormEventHandler<HTMLFormElement>;
  onForgotPassword: MouseEventHandler<HTMLElement>;
}

export interface FormValues {
  [EMAIL_FIELD]: string;
  [PASSWORD_FIELD]: string;
}

export const validationSchema = object().shape({
  [EMAIL_FIELD]: string().email('Please enter a valid email address').required('Email is required'),
  [PASSWORD_FIELD]: string().required('Password is required'),
});

export const LoginForm = ({ onSubmitHandler, onForgotPassword }: LoginFormProps) => {
  const {
    setValue,
    formState: { isSubmitting },
  } = useFormContext<FormValues>();

  useEffect(() => {
    const { login_hint, email } = qs.parse(window.location.search);

    if (typeof login_hint === 'string') {
      setValue(EMAIL_FIELD, login_hint);
      return;
    }
    if (typeof email === 'string') {
      setValue(EMAIL_FIELD, email);
      return;
    }
  }, [setValue]);

  return (
    <>
      <Typography variant="h2" gutterBottom data-test="login-text">
        Log in
      </Typography>
      <form onSubmit={onSubmitHandler} data-test="login-form" noValidate>
        <FormContainer fullPage>
          <Grid container spacing={3}>
            <Grid size={12}>
              <RHFTextField
                name={EMAIL_FIELD}
                type="email"
                label="Email"
                placeholder="Enter your email"
                required
                fullWidth
                margin="dense"
                autoComplete="login"
                data-test="email"
              />
              <RHFTextField
                name={PASSWORD_FIELD}
                type="password"
                label="Password"
                placeholder="Enter your password"
                required
                fullWidth
                margin="dense"
                autoComplete="password"
                data-test="password"
              />
            </Grid>
            <Grid size={12}>
              <Grid container spacing={2} direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                <Grid>
                  <Button variant="text" onClick={onForgotPassword} data-test="forgot-password">
                    Forgot Password
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    type="submit"
                    color="primary"
                    loading={isSubmitting}
                    data-test="sign-in-button"
                    disabled={isSubmitting}
                  >
                    Sign in
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </FormContainer>
      </form>
    </>
  );
};
